/**
 * @fileoverview Rows for the pricing page's plan-comparison table.
 *
 * Why this exists: the table used to be a hardcoded array in Pricing.js, and it
 * drifted from what customers were actually sold. On the same page, the Clerk
 * cards said "unlimited solves" and "10 saved models" while the table underneath
 * said "up to 500 monthly solves" and "up to 5 saved models". Two of those rows
 * under-promised a plan someone had already paid for, and one invented a cap
 * that has never existed in the backend at all.
 *
 * So every cell that CAN be derived is derived, from the same GET /plans catalog
 * that drives entitlement gating. Only the row labels and the genuinely
 * editorial rows (support tier, REST API) stay written down here.
 *
 * Rows are declared, not computed inline, so the shape stays reviewable:
 *   - kind 'limit'   — read a number out of the plan's `limits`
 *   - kind 'feature' — read a boolean out of the plan's `features`
 *   - kind 'static'  — editorial; not represented in the catalog
 *
 * The Developer tier is not purchasable yet and is absent from the catalog, so
 * its column reads from the declared values below (see ComingSoonBanner on the
 * page). It is not permanently static, though: the moment GET /plans publishes a
 * `developer` tier, that column derives like every other one, and the declared
 * values become a fallback for anything the catalog doesn't answer.
 */

import { findPlanInCatalog } from 'functions/entitlements/featureEntitlements';

/** Shown when a plan expresses "no limit" (the backend sends null). */
export const UNLIMITED = 'Unlimited';

/**
 * Read a dotted path out of an object without throwing on a missing branch.
 * @param {object} obj
 * @param {string} path e.g. 'locations.maxRows'
 */
function readPath(obj, path) {
  return path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
}

/**
 * Render one derived cell.
 *
 * Returns `null` when the catalog can't answer, which the table renders as an
 * em dash rather than as a `false` cross — "we don't know" and "you don't get
 * it" are different claims, and only one of them is safe to make on a page
 * where people are entering card details.
 */
function limitCell(plan, path, format) {
  if (!plan) return null;
  const raw = readPath(plan.limits, path);
  // The backend encodes "no cap" as null; absent means the catalog didn't say.
  if (raw === null) return UNLIMITED;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
  return format ? format(raw) : `Up to ${raw}`;
}

function featureCell(plan, key) {
  if (!plan) return null;
  const features = plan.features;
  if (!features || typeof features !== 'object') return null;
  if (!Object.prototype.hasOwnProperty.call(features, key)) return null;
  return features[key] === true;
}

/**
 * Row declarations. `starterStatic`/`professionalStatic` are used only for
 * `kind: 'static'` rows; derived rows ignore them.
 */
const ROWS = [
  // Two optimizers exist: vehicle-routing and pickup-delivery. The old
  // hardcoded table also listed 'Traveling Salesman' as a third — there is no
  // such model; TSP is vehicle routing with one vehicle. Removed rather than
  // carried forward.
  /* These three are entitlement keys in the backend registry, not editorial
     claims — `model_type:vehicle-routing`, `model_type:pickup-delivery` and
     `time_windows` are all gateable. They read as derived rows with declared
     fallbacks so that gating one changes this table by itself.

     Being honest about the limit: `tier_features()` publishes only what a tier
     DECLARES, and no tier declares these, so `GET /plans` does not mention them
     and every cell falls back to the declared `true` below — today's output,
     unchanged. They become live the day the backend either declares one on a
     tier or publishes the registry's resolved defaults. Until then this is a
     socket, not a guarantee, and saying so is the point. */
  { label: 'Vehicle Routing optimizer', kind: 'feature', featureKey: 'model_type:vehicle-routing', starter: true, professional: true, developer: true },
  { label: 'Pickup & Dropoff optimizer', kind: 'feature', featureKey: 'model_type:pickup-delivery', starter: true, professional: true, developer: true },
  // A constraint, not an optimizer — the old table listed it beside the model
  // names, which read as if windows were a third model.
  { label: 'Time windows', kind: 'feature', featureKey: 'time_windows', starter: true, professional: true, developer: true },
  {
    label: 'Stops per run',
    kind: 'limit',
    path: 'locations.maxRows',
    developer: UNLIMITED,
  },
  {
    label: 'Vehicles per run',
    kind: 'limit',
    path: 'vehicles.maxRows',
    developer: UNLIMITED,
  },
  {
    label: 'Saved scenarios',
    kind: 'limit',
    path: 'saved_scenarios',
    developer: UNLIMITED,
  },
  /* The library: locations, vehicles and vehicle types kept on the account and
     reusable in any scenario. Three rows rather than one, because the caps are
     genuinely per type — a single "library items" number would be a figure no
     plan actually enforces.

     `omitWhenUnknown` because these keys reach a given environment only when
     the backend publishes them. Until then the honest options are an em dash
     or nothing, and a pricing table is the one page where a row of dashes
     invites the reader to wonder what they are not being told. */
  {
    label: 'Saved locations in your library',
    kind: 'limit',
    path: 'saved_items_locations',
    omitWhenUnknown: true,
    developer: UNLIMITED,
  },
  {
    label: 'Saved vehicles in your library',
    kind: 'limit',
    path: 'saved_items_vehicles',
    omitWhenUnknown: true,
    developer: UNLIMITED,
  },
  {
    label: 'Saved vehicle types in your library',
    kind: 'limit',
    path: 'saved_items_vehicle_types',
    omitWhenUnknown: true,
    developer: UNLIMITED,
  },
  {
    label: 'Run history',
    kind: 'limit',
    path: 'run_history_days',
    format: (n) => `${n} days`,
    developer: UNLIMITED,
  },
  {
    label: 'Road distances and drive times',
    kind: 'feature',
    featureKey: 'road_network_costs',
    developer: true,
  },
  {
    label: 'Multi-day routes',
    kind: 'feature',
    featureKey: 'multi_day',
    developer: true,
  },
  {
    label: 'Mid-route resupply',
    kind: 'feature',
    featureKey: 'resupply',
    developer: true,
  },
  {
    label: 'Mixed vehicle types',
    kind: 'feature',
    featureKey: 'heterogeneous_travel_costs',
    developer: true,
  },
  {
    label: 'Lock a stop to a vehicle or time',
    kind: 'feature',
    featureKey: 'fixed_assignments',
    developer: true,
  },
  { label: 'REST API', kind: 'static', starter: false, professional: false, developer: true },
  { label: 'Support', kind: 'static', starter: 'Community', professional: 'Email', developer: 'Dedicated' },
];

/**
 * Build the comparison rows for the pricing table.
 *
 * @param {Array<object>|null|undefined} catalog - Plans from GET /plans.
 * @returns {Array<{label: string, starter: *, professional: *, developer: *}>}
 *   Cell values are `true`/`false` (tick/cross), a string, or `null` (unknown).
 */
export function buildPlanComparisonRows(catalog) {
  const free = findPlanInCatalog(catalog, 'free');
  const professional = findPlanInCatalog(catalog, 'professional');

  /*
  The Developer tier is the one this file was warning about.

  Every other column is derived because a written-down table drifts from what
  customers were actually sold — that is the whole reason this module exists.
  The Developer column was exempt only because the plan did not exist: it is not
  purchasable, it is absent from GET /plans, and there was nothing to read. So it
  was written down, and what is written down are marketing values — unlimited
  everything, every feature ticked.

  The day the backend publishes a `developer` tier with real API caps, those
  hardcoded cells become precisely the bug this file exists to prevent: a pricing
  table promising more than the plan enforces. Deriving-with-fallback makes that
  switch automatic instead of something someone has to remember on launch day.

  Until the tier ships, `developer` is null here and every cell resolves to the
  declared value — today's output, unchanged.
  */
  const developer = findPlanInCatalog(catalog, 'developer');

  /**
   * A derived cell, or the row's declared value when the catalog can't answer.
   *
   * `derived` is a thunk so the lookup is skipped while there is no plan to read
   * — and, more importantly, so a plan that exists but omits this particular key
   * (which limitCell/featureCell report as null) falls back to the declared
   * value rather than rendering an em dash. A dash in the Developer column of a
   * live pricing page reads as something being withheld.
   */
  const cell = (plan, derived, declared) => {
    // `undefined` means the row declares no fallback for this column, which is
    // not the same claim as "we don't know" — the table renders both as an em
    // dash, but only null says so deliberately.
    const fallback = declared === undefined ? null : declared;
    if (!plan) return fallback;
    const value = derived();
    return value === null || value === undefined ? fallback : value;
  };

  return ROWS.map((row) => {
    if (row.kind === 'static') {
      return {
        label: row.label,
        starter: row.starter,
        professional: row.professional,
        // Editorial by definition — no catalog field backs "Support: Dedicated".
        developer: row.developer,
      };
    }
    if (row.kind === 'limit') {
      return {
        label: row.label,
        starter: cell(free, () => limitCell(free, row.path, row.format), row.starter),
        professional: cell(professional, () => limitCell(professional, row.path, row.format), row.professional),
        developer: cell(developer, () => limitCell(developer, row.path, row.format), row.developer),
      };
    }
    return {
      label: row.label,
      starter: cell(free, () => featureCell(free, row.featureKey), row.starter),
      professional: cell(professional, () => featureCell(professional, row.featureKey), row.professional),
      developer: cell(developer, () => featureCell(developer, row.featureKey), row.developer),
    };
  }).filter((built, index) => {
    /* Drop a row only if it asked to be dropped AND neither purchasable plan
       could answer it. Both halves matter: a row that simply hasn't loaded yet
       must still hold its place, or the table would shed rows while the
       catalog is in flight and grow them back underneath the reader. */
    if (!ROWS[index].omitWhenUnknown) return true;
    return built.starter !== null || built.professional !== null;
  });
}

export default buildPlanComparisonRows;
